import React from "react";

// ─── Field Reference ──────────────────────────────────────────────────────────

interface FieldDef {
  key: string;
  label: string;
  description: string;
}

interface ScopeDef {
  scope: string;
  label: string;
  fields: FieldDef[];
}

const SCOPES: ScopeDef[] = [
  {
    scope: "client",
    label: "Client",
    fields: [
      { key: "id", label: "ID", description: "Unique Jobber client identifier." },
      { key: "name", label: "Name", description: "Full name of the client." },
      { key: "phone", label: "Phone", description: "Primary phone number." },
      { key: "email", label: "Email", description: "Email address on file." },
      { key: "billingAddress", label: "Billing Address", description: "Billing address — street, city, state, zip." }
    ]
  },
  {
    scope: "job",
    label: "Job",
    fields: [
      { key: "id", label: "ID", description: "Unique Jobber job identifier." },
      { key: "jobNumber", label: "Job Number", description: "Human-readable job number shown in Jobber." },
      { key: "title", label: "Title", description: "Job title or service description." },
      { key: "status", label: "Status", description: "Current status — e.g. scheduled, in_progress, completed." },
      { key: "scheduledStart", label: "Scheduled Start", description: "Scheduled start date and time for the job." },
      { key: "clientName", label: "Client Name", description: "Name of the client associated with this job." },
      { key: "assignedTo", label: "Assigned To", description: "Technician or team member assigned to this job." }
    ]
  },
  {
    scope: "quote",
    label: "Quote",
    fields: [
      { key: "id", label: "ID", description: "Unique Jobber quote identifier." },
      { key: "quoteNumber", label: "Quote Number", description: "Human-readable quote number." },
      { key: "title", label: "Title", description: "Quote title or service description." },
      { key: "total", label: "Total", description: "Total quoted amount." },
      { key: "status", label: "Status", description: "Quote status — draft, sent, approved, declined." }
    ]
  },
  {
    scope: "invoice",
    label: "Invoice",
    fields: [
      { key: "id", label: "ID", description: "Unique Jobber invoice identifier." },
      { key: "invoiceNumber", label: "Invoice Number", description: "Human-readable invoice number." },
      { key: "subject", label: "Subject", description: "Invoice subject line." },
      { key: "total", label: "Total", description: "Total invoice amount." },
      { key: "balance", label: "Balance", description: "Outstanding balance due." },
      { key: "dueDate", label: "Due Date", description: "Payment due date." },
      { key: "status", label: "Status", description: "Invoice status — draft, sent, paid, overdue." }
    ]
  }
];

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  teamId: number;
  selectedConfigId: number;
}

export default function JobberDataMapping(_props: Props) {
  return (
    <div>
      <p className="text-sm text-zinc-600 mb-4">
        Reference these field paths in workflow variables and HTTP POST nodes using the template{" "}
        <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-xs font-mono text-zinc-700">{"{{jobber.{scope}.{field}}}"}</code>.
      </p>

      <div className="space-y-6">
        {SCOPES.map((scope) => (
          <div key={scope.scope}>
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">{scope.label}</div>
            <div className="border border-zinc-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-zinc-600 w-48">Variable</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-zinc-600 w-32">Field</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-zinc-600">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {scope.fields.map((field) => (
                    <tr key={field.key} className="hover:bg-zinc-50 transition-colors">
                      <td className="px-4 py-2">
                        <code className="text-xs font-mono text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
                          {`{jobber.${scope.scope}.${field.key}}`}
                        </code>
                      </td>
                      <td className="px-4 py-2 text-xs font-medium text-zinc-700">{field.label}</td>
                      <td className="px-4 py-2 text-xs text-zinc-500">{field.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded bg-zinc-50 border border-zinc-200 p-4 text-xs text-zinc-600">
        <strong>Example — pass job data to an HTTP POST node:</strong>
        <pre className="mt-2 bg-zinc-900 text-zinc-100 rounded p-3 font-mono text-[11px] overflow-x-auto">{`{
  "job_number":  "{jobber.job.jobNumber}",
  "job_status":  "{jobber.job.status}",
  "technician":  "{jobber.job.assignedTo}",
  "client":      "{jobber.client.name}",
  "scheduled":   "{jobber.job.scheduledStart}"
}`}</pre>
      </div>
    </div>
  );
}
